import { Box, Card } from '@deckee/deck-hand';
import React, { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { transitionToNextRoute } from '../../../../../services/route/route-navigation';
import { captureEvent } from '../../../../../services/event-capture';
import { useAppDispatch } from '../../../../../services/store';
import { moveMapToLocation } from '../../../../../services/store/modules/app-controls';
import { telemetry } from '../../../../../services/backend';

import {
  hideNotification,
  NotificationModel,
  selectAllNotifications,
  setNotificationAsRead,
  setSelectedNotification,
} from '../../../../../services/store/modules/notifications';
import NotificationItem from '../../components/NotificationItem';
import HideNotificationSheet from '../../components/HideNotificationSheet';
import AlertsPage, { AlertItem } from './AlertsPage';
import useActiveFloatPlan from '../../../../hooks/use-active-floatplan';
import {
  circleWrapper,
  getWeatherAlertIcon,
  siren,
} from '../../../../shared/vector-images';
import { setAlertNotificationAsRead } from '../../../../../services/member/alerts';
import { openWithInAppBrowser } from '../../../../../services/in-app-browser';
import { NotificationItemTypeEnum } from '@deckee/api-app-main';

const StyledAlertItem = styled(NotificationItem)`
  &:last-child {
    margin-bottom: 0;
  }
`;

export const getNotificationIconType = (
  type: string,
  subtype: string,
): 'weather' | 'alert' | 'notice' => {
  if (type === 'weather' && subtype !== 'general') {
    return 'weather';
  }
  if (type === 'notice') {
    return 'notice';
  }
  return 'alert';
};

type HideTarget = {
  id: string;
  notificationType?: string;
};

const NotificationsFeedPage: FC = () => {
  const notifications = useSelector(selectAllNotifications);
  const dispatch = useAppDispatch();

  const history = useHistory();
  const { activeFloatPlanId } = useActiveFloatPlan();
  const [itemToHide, setItemToHide] = useState<HideTarget | null>(null);
  const [hiddenAlertIds, setHiddenAlertIds] = useState<string[]>([]);
  const [isHiding, setIsHiding] = useState(false);

  const handleOpenHideMenu = (item: HideTarget) => {
    captureEvent({
      type: 'notifications: open hide menu',
      payload: { notificationId: item.id },
      sendToAmplitude: true,
    });
    setItemToHide(item);
  };

  const handleCancelHide = () => {
    captureEvent({
      type: 'notifications: hide notification cancelled',
      payload: itemToHide ? { notificationId: itemToHide.id } : {},
      sendToAmplitude: true,
    });
    setItemToHide(null);
  };

  const handleConfirmHide = async () => {
    if (!itemToHide) {
      return;
    }

    setIsHiding(true);
    captureEvent({
      type: 'notifications: hide notification confirmed',
      payload: { notificationId: itemToHide.id },
      sendToAmplitude: true,
    });

    try {
      await dispatch(
        hideNotification({
          id: itemToHide.id,
          notificationType: itemToHide.notificationType,
        }),
      ).unwrap();

      if (itemToHide.notificationType === 'alert') {
        setHiddenAlertIds((previous) => [...previous, itemToHide.id]);
      }
      setItemToHide(null);
    } catch (error) {
      telemetry(
        telemetry.ERROR,
        `[NotificationsFeedPage.handleConfirmHide] ${JSON.stringify(error)}`,
      );
    } finally {
      setIsHiding(false);
    }
  };

  const handleSelectAlert = async (alert: AlertItem) => {
    let eventType = 'notifications: select alert';
    if (alert.type === 'weather')
      eventType = 'notifications: select weather warning';
    captureEvent({
      type: eventType,
      payload: {
        ...alert,
      },
      sendToAmplitude: true,
    });

    setAlertNotificationAsRead(alert.id);

    if (alert.custom_navigation) {
      transitionToNextRoute(
        alert.custom_navigation,
        alert.custom_navigation_parameters,
      );
    } else {
      history.push(`/maps/notifications/alerts/${alert.id}`);
    }
  };
  const handleSelectNotification = (notification: NotificationModel) => {
    captureEvent({
      type: 'notifications: select notification',
      payload: {
        ...notification,
      },
      sendToAmplitude: true,
    });

    dispatch(setSelectedNotification(notification.id));
    dispatch(setNotificationAsRead());

    switch (notification.type) {
      case NotificationItemTypeEnum.OfflineIncidentReportAvailable:
        if (notification.data?.latitude && notification.data?.longitude) {
          dispatch(
            moveMapToLocation({
              center: {
                lat: notification.data.latitude,
                lon: notification.data.longitude,
              },
            }),
          );
        }
        history.push('/maps/incident-report', {
          description: notification.data?.description,
          incidentDate: notification.data?.incidentDate,
          offlineInitiated: true,
        });
        break;

      case NotificationItemTypeEnum.IncidentReported:
        if (notification.data?.itemType && notification.data?.itemId) {
          history.push(
            `/maps/incident-report/view/${notification.data.itemId}`,
          );
        }
        break;

      case NotificationItemTypeEnum.FloatPlanNotification:
        history.push(`/maps/float-plan/${notification?.data?.itemId}`);
        break;

      case NotificationItemTypeEnum.WatercraftNotification:
        history.push(
          `/maps/settings/account/boats/edit/${notification?.data?.itemId}`,
        );
        break;

      case NotificationItemTypeEnum.SharedMarker:
        if (notification.data?.itemType && notification.data?.itemId) {
          switch (notification.data?.itemType) {
            case 'trip':
              history.push(`/maps/trips/summary/${notification.data.itemId}`);
              break;
            case 'floatPlan':
              history.push(`/maps/float-plan/${notification.data.itemId}`);
              break;
            default:
              if (notification.data?.viewDetails) {
                history.push(
                  `/maps/marker/view/${notification.data.itemType}/${notification.data.itemId}`,
                );
              } else {
                history.push(
                  `/maps/marker/summary/${notification.data.itemType}/${notification.data.itemId}`,
                );
              }
          }
        }
        break;

      case NotificationItemTypeEnum.ActivateSignal:
      case NotificationItemTypeEnum.SignalShared:
      case NotificationItemTypeEnum.UserFollowing:
        history.push('/maps/signal/friends-sheet');
        break;

      case NotificationItemTypeEnum.ExternalLink:
      case NotificationItemTypeEnum.ExternalLinkPushNotification:
        openWithInAppBrowser({
          url: notification.data?.url,
        });
        break;

      case NotificationItemTypeEnum.InformationNotice:
        if (notification.externalId) {
          history.push(`/maps/information-notice/${notification.externalId}`, {
            informationNoticeSource: 'notification_feed',
          });
        }
        break;

      case NotificationItemTypeEnum.Generic:
      default:
        telemetry(
          telemetry.WARN,
          `No handlers for the current type', ${JSON.stringify(notification)}`,
        );
    }
  };

  const activeFloatPlanNotifications = notifications.filter(
    (notification) =>
      notification.type === NotificationItemTypeEnum.FloatPlanNotification &&
      notification.externalId === activeFloatPlanId,
  );

  const prioritizedNotifications = notifications.filter(
    (notification) =>
      notification.data?.requiredNotification && !notification.read,
  );

  return (
    <>
      {activeFloatPlanNotifications.length > 0 && (
        <>
          <Card.Heading size="compact" pt="5px !important">
            Active plan notifications
          </Card.Heading>

          <Box p="2">
            {activeFloatPlanNotifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                title={notification.heading}
                description={notification.description || ''}
                read={notification.read}
                timestamp={notification.createdAt.toISOString()}
                imagePath={notification.iconImage || undefined}
                onClick={() => {
                  handleSelectNotification(notification);
                }}
                onMorePress={() => {
                  handleOpenHideMenu({ id: notification.id });
                }}
              />
            ))}
          </Box>
        </>
      )}

      <AlertsPage
        prioritizedNotifications={prioritizedNotifications}
        handleSelectNotification={handleSelectNotification}
        handleSelectAlert={handleSelectAlert}
        hideHeadings
        hideGeoFenceMessage
        hiddenAlertIds={hiddenAlertIds}
        onMorePressAlert={(alert) => {
          handleOpenHideMenu({ id: alert.id, notificationType: 'alert' });
        }}
        onMorePressNotification={(notification) => {
          handleOpenHideMenu({ id: notification.id });
        }}
      />

      {notifications.length > 0 && (
        <>
          <Card.Heading size="compact" pt="5px !important">
            All notifications
          </Card.Heading>

          <Box p="2">
            {notifications.map((notification) => {
              if (notification?.notificationType === 'alert') {
                return (
                  <StyledAlertItem
                    key={notification.id}
                    read={notification.read}
                    title={notification.title}
                    timestamp={
                      notification.updated_at || notification.created_at
                    }
                    icon={getWeatherAlertIcon(notification.subtype)}
                    iconType={getNotificationIconType(
                      notification.type,
                      notification.subtype as unknown as string,
                    )}
                    iconUrl={notification.icon_url}
                    onClick={() => {
                      handleSelectAlert(notification as unknown as AlertItem);
                    }}
                    onMorePress={() => {
                      handleOpenHideMenu({
                        id: notification.id,
                        notificationType: 'alert',
                      });
                    }}
                  />
                );
              }
              return (
                <NotificationItem
                  key={notification.id}
                  title={notification.heading}
                  description={notification.description || ''}
                  read={notification.read}
                  timestamp={notification.createdAt.toISOString()}
                  imagePath={notification.iconImage || undefined}
                  icon={
                    notification.data?.requiredNotification
                      ? circleWrapper(siren())
                      : undefined
                  }
                  iconType={
                    notification.data?.requiredNotification
                      ? 'prioritized'
                      : undefined
                  }
                  onClick={() => {
                    handleSelectNotification(notification);
                  }}
                  onMorePress={() => {
                    handleOpenHideMenu({ id: notification.id });
                  }}
                />
              );
            })}
          </Box>
        </>
      )}

      {itemToHide && (
        <HideNotificationSheet
          onConfirm={handleConfirmHide}
          onCancel={handleCancelHide}
          isLoading={isHiding}
        />
      )}
    </>
  );
};

export default NotificationsFeedPage;
